<template>
  <div class="search-menu" :class="{ open: open }" ref="searchMenu" tabindex="-1" @focusout="checkFocusOut($event)">
    <div class="selected primary-input" :class="{ open: open && options.length > 0, invalid: invalid }" @click="open = true">
      <input type="search" ref="searchMenuInput" :placeholder="placeholder" v-model="searchText" :tabindex="tabindex" :class="{ 'no-outline': noOutline }" @keydown="moveOption($event)" @focus="handleFocus()" />
      <label v-if="label">{{ label }}</label>
      <label v-if="required && invalid" class="required">Required Field</label>
      <img
        v-if="showExtLink"
        src="../../assets/external-link.svg"
        alt=""
        class="external-link"
        @click.stop="$emit('navigateToLink')" />
      <img v-else class="search-icon" src="../../assets/search.svg" alt="" />
    </div>
    <div class="items" ref="searchMenuItems" :class="{ selectHide: !open || options.length === 0 }">
      <div v-for="(option, i) of options" :key="i" @click="selectOption(option)" :class="{ hovering: focusedIndex == i }" @mouseover="focusedIndex = i">
        <img v-if="option.type == 'company'" src="../../assets/company.svg" alt="" />
        <img v-if="option.type == 'contact'" src="../../assets/individual.svg" alt="" />
        <img v-if="option.type == 'new'" src="../../assets/add-contact-plus.svg" alt="" />
        {{ option.text }}
        <span v-if="option.inactive" class="inactive-tag">(Inactive)</span>
        <div v-if="option.type != 'new'" class="infoBubble" @click.stop="" @mouseover="getContactInfo(option)">
          <img id="infoIcon" src="../../assets/info-bubble.svg" />
          <img id="infoIconHover" src="../../assets/info-bubble-hover.svg" />
          <div class="info-popup">
            <img src="../../assets/spinner.svg" class="spinner" v-if="option.type == 'company' && companyLoading" />
            <img src="../../assets/spinner.svg" class="spinner" v-if="option.type == 'contact' && contactLoading" />
            <div v-if="option.type == 'company' && !companyLoading && companyResult">
              <span class="name">{{ renderCompanyInfo.name }}<br /></span>
              <span v-if="renderCompanyInfo.addressLine1">{{ renderCompanyInfo.addressLine1 }}<br /></span>
              <span v-if="renderCompanyInfo.addressLine2">{{ renderCompanyInfo.addressLine2 }}<br /></span>
              <span v-for="(phone, index) in renderCompanyInfo.phones">{{ phone.NUMBERDISPLAY }}<br v-if="index < renderCompanyInfo.phones.length - 1" /></span>
              <span v-for="contact in renderCompanyInfo.contacts.slice(0, 4)" class="contact"><img src="../../assets/individual.svg" />{{ contact.FIRSTNAME }} {{ contact.LASTNAME }}<br /></span>
            </div>
            <div v-if="option.type == 'contact' && !contactLoading && contactResult">
              <span class="name">{{ renderContactInfo.name }}</span>
              <span v-if="renderContactInfo.addressLine1">{{ renderContactInfo.addressLine1 }}<br /></span>
              <span v-if="renderContactInfo.addressLine2">{{ renderContactInfo.addressLine2 }}</span>
              <span v-for="(phone, index) in renderContactInfo.phones">{{ phone.NUMBERDISPLAY }}<br v-if="index < renderContactInfo.phones.length - 1" /></span>
              <span v-if="renderContactInfo.companyName" class="contact"><img src="../../assets/company.svg" />{{ renderContactInfo.companyName }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import useCompanyQuery from '@/composables/useCompanyQuery';
import useContactQuery from '@/composables/useContactQuery';
import useDecryptSymbols from '@/composables/useDecryptSymbols';

export default {
  name: 'SearchMenu',
  emits: ['update:modelValue', 'updateContact', 'forceOptionsUpdate', 'blurred', 'navigateToLink'],
  props: {
    options: {
      type: Array,
      required: true,
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    label: {
      type: String,
      required: false,
      default: '',
    },
    modelValue: {
      type: String,
      required: false,
      default: '',
    },
    tabindex: {
      type: String,
      required: false,
      default: '0',
    },
    noOutline: {
      type: Boolean,
      required: false,
      default: false,
    },
    invalid: {
      type: Boolean,
      required: false,
      default: false,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    clearOnFocus: {
      type: Boolean,
      required: false,
      default: false,
    },
    showExtLink: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  setup() {
    const queryCompany = useCompanyQuery();
    const queryContact = useContactQuery();
    const { decryptSymbols } = useDecryptSymbols();

    return {
      queryCompany,
      queryContact,
      decryptSymbols,
    };
  },
  data() {
    return {
      open: false,
      focusedIndex: null,
      companyQuery: null,
      contactQuery: null,
      infoCompanyId: null,
      infoContactId: null,
    };
  },
  computed: {
    searchText: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    companyResult() {
      return this.companyQuery?.result?.company || null;
    },
    companyLoading() {
      return this.companyQuery?.loading || false;
    },
    companyError() {
      return this.companyQuery?.error || null;
    },
    contactResult() {
      return this.contactQuery?.result?.contact || null;
    },
    contactLoading() {
      return this.contactQuery?.loading || false;
    },
    contactError() {
      return this.contactQuery?.error || null;
    },
    renderCompanyInfo() {
      const info = this.decryptSymbols(this.companyResult || {});
      if (!info) return {};
      const addressLine1 = info.address?.LINE1;
      const addressLine2 = `${info.address?.CITY}, ${info.address?.STATE} ${info.address?.POSTALCODE}`;
      const phones = [];
      if (info.phones) {
        info.phones.forEach((phone) => {
          if ((phone.TYPEID === 'C3AE9586-6158-4D0D-9FAF-253CB0152F18' || phone.TYPEID === '17057633-4B31-4AE8-BE33-ED27C7FD1B12') && !phones.some((p) => p.TYPEID === phone.TYPEID) && phone.NUMBERDISPLAY) {
            phones.push(phone);
          }
        });
      }

      return {
        name: info.NAME,
        addressLine1: addressLine1,
        addressLine2: addressLine2,
        phones: phones,
        contacts: (info.contacts || []).filter((contact) => !contact.INACTIVE),
      };
    },
    renderContactInfo() {
      const info = this.decryptSymbols(this.contactResult || {});
      if (!info) return {};
      const addressLine1 = info.address?.LINE1;
      const addressLine2 = `${info.address?.CITY}, ${info.address?.STATE} ${info.address?.POSTALCODE}`;
      const phones = [];
      if (info.phones) {
        info.phones.forEach((phone) => {
          if ((phone.TYPEID === 'C3AE9586-6158-4D0D-9FAF-253CB0152F18' || phone.TYPEID === '511DF006-2CDB-40B5-8D1A-B313C873DD1C') && !phones.some((p) => p.TYPEID === phone.TYPEID) && phone.NUMBERDISPLAY) {
            phones.push(phone);
          }
        });
      }
      return {
        name: info.FIRSTNAME + ' ' + info.LASTNAME,
        addressLine1: addressLine1,
        addressLine2: addressLine2,
        phones: phones,
        companyName: info.company?.NAME || '',
      };
    },
  },
  methods: {
    handleFocus() {
      if (this.clearOnFocus) {
        this.searchText = '';
        this.$emit('updateContact', null);
      }
      this.$emit('forceOptionsUpdate');
      this.open = true;
      this.focusedIndex = null;
    },
    selectOption(option) {
      this.$emit('updateContact', option);
      this.open = false;
    },
    checkFocusOut(event) {
      if (!this.$refs.searchMenu || !this.$refs.searchMenuItems) return;
      if (!this.$refs.searchMenuItems.contains(event.relatedTarget) && !this.$refs.searchMenu.contains(event.relatedTarget)) {
        this.open = false;
        this.$emit('blurred');
      }
    },
    moveOption(event) {
      let direction = '';
      if (event.key === 'ArrowDown') direction = 'down';
      else if (event.key === 'ArrowUp') {
        direction = 'up';
        event.preventDefault();
      } else if (event.key === 'Enter') {
        if (this.focusedIndex !== null) {
          this.selectOption(this.options[this.focusedIndex]);
          return;
        } else if (this.options.length > 0) {
          // Prefer the first real result over "Add New" / "New Company" when available
          const firstMatch = this.options.find((option) => option.type !== 'new');
          this.selectOption(firstMatch || this.options[0]);
          return;
        }
      } else {
        this.focusedIndex = null;
        return;
      }

      if (this.focusedIndex === null && direction == 'down') this.focusedIndex = 0;
      else {
        this.focusedIndex = direction === 'down' ? this.focusedIndex + 1 : this.focusedIndex - 1;
      }

      if (this.focusedIndex < 0) this.focusedIndex = null;
      if (this.focusedIndex >= this.options.length) this.focusedIndex = this.options.length - 1;
    },
    getContactInfo(option) {
      if (option.type === 'company') {
        if (this.infoCompanyId !== option.value) {
          this.infoCompanyId = option.value;
        }
      } else if (option.type === 'contact') {
        if (this.infoContactId !== option.value) {
          this.infoContactId = option.value;
        }
      }
    },
    formatPhone(phone) {
      return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    },
  },
  watch: {
    infoCompanyId(newId) {
      if (newId) {
        this.companyQuery = this.queryCompany(newId);
      } else {
        this.companyQuery = null;
      }
    },
    infoContactId(newId) {
      if (newId) {
        this.contactQuery = this.queryContact(newId);
      } else {
        this.contactQuery = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.search-menu {
  position: relative;
  text-align: left;
  outline: none;

  &.open {
    z-index: 1001;
  }

  .selected {
    &.open input {
      border-radius: $borderRadius4 $borderRadius4 0px 0px;
    }

    .search-icon,
    .external-link {
      position: absolute;
      top: 0.75rem;
      right: 0.8rem;
      height: 1.5rem;
    }

    .external-link {
      cursor: pointer;
    }
  }

  .items {
    color: $textColor87;
    border-radius: 0px 0px $borderRadius4 $borderRadius4;
    overflow: visible;
    position: absolute;
    background-color: white;
    left: 0;
    right: 0;
    z-index: 1001;
    border: 1px solid $inputBorderColor;
    border-top: none;
    max-height: 25rem;

    & > div {
      color: $textColor87;
      font-size: $fontSize14;
      padding: 0.6rem 2.5rem 0.6rem 0.7rem;
      cursor: pointer;
      user-select: none;
      display: flex;
      align-items: center;
      position: relative;

      img {
        margin-right: 0.5rem;
      }

      .inactive-tag {
        color: $textColor38;
        margin-left: 0.3rem;
      }

      .infoBubble {
        display: none;
        position: absolute;
        right: 0.5rem;
        top: 0.6rem;
        height: 1.5rem;

        #infoIcon {
          display: block;
          width: 80%;
          height: 80%;
        }

        #infoIconHover {
          display: none;
          width: 80%;
          height: 80%;
        }

        .info-popup {
          display: none;
          position: absolute;
          background-color: white;
          box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.15);
          color: black;
          padding: 0.1rem;
          border-radius: $borderRadius4;
          bottom: 1.9rem;
          right: 0.3rem;
          min-width: 2rem;
          min-height: 2rem;
          font-size: $fontSize11;
          z-index: 1;
          padding: 0.8rem;

          .spinner {
            width: 2rem;
            height: 2rem;
            padding: 1.5rem;
          }

          span {
            display: block;
            text-wrap: nowrap;
          }

          .name {
            font-weight: bold;
            font-size: $fontSize12;
          }

          .contact {
            font-size: $fontSize10;
            display: flex;
            align-items: center;
            padding-top: 0.3rem;

            img {
              margin-right: 0.5rem;
            }
          }
        }

        &:hover {
          #infoIcon {
            display: none;
          }

          #infoIconHover {
            display: block;
          }

          .info-popup {
            display: block;
          }
        }
      }

      &.hovering {
        background-color: $arrowGrayColor;

        .infoBubble {
          display: block;
        }
      }
    }
  }
}

.selectHide {
  display: none;
}
</style>
